import { ADVERT_FORMAT_TYPES } from '@web-platform/shared-universal-constants/src/adverts';
import { generateAdvertIdFromRadarCreative } from '@web-platform/shared-universal-utils-frontend/src/helpers/generateAdvertId';

import type {
  RadarCreative,
  RadarCreativeFlightsBanner,
  RadarCreativeFlightsFlexibleSearch,
  RadarCreativeFlightsInlineBrandBanner,
  RadarCreativeFlightsInlineCompetitorBrandBanner,
  RadarCreativeFlightsInlineItinerary,
  RadarCreativeItineraryInlinePlus,
  RadarCreativePricedBrandInline,
  RadarCreativeUnpricedBrandInline,
} from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarCreativeTypes';
import type {
  AnalyticsProperties,
  CombinedResultsFlightsInlineAdvert,
  CreativeFlightsBanner,
  CreativeFlightsInlineBrandBanner,
  CreativeFlightsInlineItinerary,
  CreativeFlightsPricedBrandInline,
  CreativeFlightsUnpricedBrandInline,
  CreativeFlightsFlexibleSearch,
  CreativeItineraryInlinePlus,
} from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/combinedResultsFlightsInline';
import type { Nullable } from '@web-platform/shared-universal-types-utils/src/utils';

const getFlightsInlineCreativeFromRadar = (
  radarCreative: RadarCreativeFlightsInlineItinerary,
): CreativeFlightsInlineItinerary => {
  const { fields, idFormat } = radarCreative;

  // We need to build up a new object because flow cannot infer the type after a spread.
  // See https://github.com/facebook/flow/issues/6108
  const {
    appIndex,
    callToActionText,
    checkoutType,
    deeplink,
    deeplinkParams,
    dimensions,
    itineraryId,
    layoutType,
    message,
    partner,
    partnerDesktopLogo,
    partnerLogo,
    pioPartnerName,
    redirectUrl,
    requestedDepth,
    selection,
    title,
    trackingModifications,
    transferType,
    useFlightsSkippyChannel,
  } = fields;

  const creativeFields = {
    appIndex,
    callToActionText,
    deeplink,
    deeplinkParams,
    itineraryId,
    message,
    partner,
    partnerDesktopLogo,
    partnerLogo,
    redirectUrl,
    removeDeeplinkIds: null,
    requestedDepth,
    selection,
    title,
    format: idFormat,
    layoutType,
    checkoutType,
    dimensions,
    trackingModifications,
    transferType,
    useFlightsSkippyChannel,
    pioPartnerName,
  };

  return creativeFields;
};

const getFlightsPricedBrandInlineCreativeFromRadar = (
  radarCreative: RadarCreativePricedBrandInline,
): CreativeFlightsPricedBrandInline => {
  const { fields, idFormat } = radarCreative;

  const {
    appIndex,
    callToActionText,
    checkoutType,
    deeplink,
    deeplinkParams,
    dimensions,
    itineraryId,
    message,
    partner,
    partnerDesktopLogo,
    partnerLogo,
    pioPartnerName,
    redirectUrl,
    requestedDepth,
    selection,
    title,
    trackingModifications,
    useFlightsSkippyChannel,
  } = fields;

  return {
    appIndex,
    callToActionText,
    deeplink,
    deeplinkParams,
    itineraryId,
    message,
    partner,
    partnerDesktopLogo,
    partnerLogo,
    pioPartnerName,
    redirectUrl,
    removeDeeplinkIds: null,
    requestedDepth,
    selection,
    title,
    checkoutType,
    dimensions,
    trackingModifications,
    useFlightsSkippyChannel,
    format: idFormat,
  };
};

const getFlightsUnpricedBrandInlineCreativeFromRadar = (
  radarCreative: RadarCreativeUnpricedBrandInline,
): CreativeFlightsUnpricedBrandInline => {
  const { fields, idFormat } = radarCreative;

  const {
    appIndex,
    callToActionText,
    ctaButtonTitleEnabled,
    message,
    partner,
    partnerDesktopLogo,
    partnerLogo,
    pioPartnerName,
    redirectUrl,
    title,
  } = fields;

  return {
    appIndex,
    callToActionText: callToActionText || null,
    ctaButtonTitleEnabled: ctaButtonTitleEnabled === 'true',
    format: idFormat,
    message,
    partnerDesktopLogo,
    partnerLogo,
    partner: partner || null,
    pioPartnerName,
    redirectUrl,
    title,
  };
};

const getBrandBannerCreativeFromRadar = (
  radarCreative: RadarCreativeFlightsInlineBrandBanner,
): CreativeFlightsInlineBrandBanner => {
  const {
    appIndex,
    callToActionText,
    headline,
    hideSponsoredLabel,
    image,
    message,
    partner,
    partnerLogo,
    pioAgencyName,
    pioPartnerName,
    redirectUrl,
  } = radarCreative.fields;

  return {
    appIndex,
    callToActionText,
    headline,
    hideSponsoredLabel: hideSponsoredLabel === 'true',
    image,
    message,
    partner,
    partnerLogo,
    pioAgencyName,
    pioPartnerName,
    redirectUrl,
    format: radarCreative.idFormat,
  };
};

const getCompetitorBrandBannerCreativeFromRadar = (
  radarCreative: RadarCreativeFlightsInlineCompetitorBrandBanner,
): CreativeFlightsInlineBrandBanner => {
  const {
    appIndex,
    callToActionText,
    headline,
    image,
    message,
    partner,
    partnerLogo,
    pioAgencyName,
    pioPartnerName,
    price,
    redirectPageType,
    redirectPlaceCode,
    redirectUrl,
  } = radarCreative.fields;

  return {
    appIndex,
    callToActionText,
    headline,
    image,
    message,
    partner,
    partnerLogo,
    pioAgencyName,
    pioPartnerName,
    redirectUrl,
    format: radarCreative.idFormat,
    redirectPlaceCode,
    price,
    redirectPageType,
  };
};

const getItineraryInlinePlusCreativeFromRadar = (
  radarCreative: RadarCreativeItineraryInlinePlus,
): CreativeItineraryInlinePlus => {
  const { fields, idFormat } = radarCreative;

  // We need to build up a new object because flow cannot infer the type after a spread.
  // See https://github.com/facebook/flow/issues/6108
  const {
    appIndex,
    backgroundColour,
    callToActionText = null,
    carriersCriteria,
    checkoutType,
    deeplink = null,
    dimensions,
    excludedCarriers,
    headline,
    includedCarriers,
    itineraryId,
    message,
    moreInfoLabel,
    moreInfoTarget,
    partner,
    partnerLogo,
    partnerType,
    pioAgencyName,
    pioPartnerName,
    redirectUrl,
    requestedDepth,
    selection,
    showMoreInfo,
    subHeadline,
    textColour,
    trackingModifications,
    transferType,
    useFlightsSkippyChannel,
  } = fields;

  return {
    appIndex,
    backgroundColour,
    callToActionText,
    subHeadline,
    deeplink,
    headline,
    format: idFormat,
    itineraryId,
    partner,
    partnerLogo,
    partnerType,
    message,
    moreInfoLabel,
    moreInfoTarget,
    pioAgencyName,
    pioPartnerName,
    showMoreInfo: (showMoreInfo || '').toLowerCase() === 'true',
    redirectUrl,
    requestedDepth,
    textColour,
    selection,
    checkoutType,
    dimensions,
    trackingModifications,
    transferType,
    useFlightsSkippyChannel,
    carriersCriteria: carriersCriteria || 'all',
    includedCarriers: includedCarriers ? includedCarriers.split(',').map(Number) : [],
    excludedCarriers: excludedCarriers ? excludedCarriers.split(',').map(Number) : [],
  };
};

const getFlexibleSearchCreativeFromRadar = (
  radarCreative: RadarCreativeFlightsFlexibleSearch,
): CreativeFlightsFlexibleSearch => {
  let alternative;
  const { fields, idFormat } = radarCreative;
  try {
    if (fields.alternative) {
      alternative = JSON.parse(fields.alternative);
    }
  } catch (err) {
    alternative = undefined;
  }

  const { onlyP1 } = fields;

  return {
    ...fields,
    onlyP1: (onlyP1 || '').toLowerCase() === 'true',
    alternative,
    format: idFormat,
  };
};

const getBannerCreativeFromRadar = (
  radarCreative: RadarCreativeFlightsBanner,
): CreativeFlightsBanner => ({
  ...radarCreative.fields,
  format: radarCreative.idFormat,
});

export const transformRadarCreativeToAdvert = (
  radarCreative: RadarCreative,
): Nullable<CombinedResultsFlightsInlineAdvert> => {
  const { analyticsProperties, idFormat, performanceEventsPayloads, pixels } = radarCreative;
  const id: string = generateAdvertIdFromRadarCreative(radarCreative);

  const { id_campaign, id_creative, id_placement, id_sponsor, id_tracking, priority, service } =
    analyticsProperties;

  const analyticsPropertiesValue: AnalyticsProperties = {
    idCampaign: id_campaign,
    idCreative: id_creative,
    idPlacement: id_placement,
    idFormat,
    idSponsor: id_sponsor,
    idTracking: id_tracking,
    priority,
    service,
  };
  const advertProps = {
    id,
    position: parseInt(radarCreative.fields.appIndex, 10),
    analyticsProperties: analyticsPropertiesValue,
    pixels,
    performanceEventsPayloads,
  };
  if (idFormat === ADVERT_FORMAT_TYPES.BRAND_BANNER) {
    return {
      ...advertProps,
      creative: getBrandBannerCreativeFromRadar(radarCreative),
    };
  }

  if (idFormat === ADVERT_FORMAT_TYPES.COMPETITOR_BRAND_BANNER) {
    return {
      ...advertProps,
      creative: getCompetitorBrandBannerCreativeFromRadar(radarCreative),
    };
  }

  if (idFormat === ADVERT_FORMAT_TYPES.INLINE) {
    return {
      ...advertProps,
      creative: getFlightsInlineCreativeFromRadar(radarCreative),
    };
  }

  if (idFormat === ADVERT_FORMAT_TYPES.PRICED_BRAND_INLINE) {
    return {
      ...advertProps,
      creative: getFlightsPricedBrandInlineCreativeFromRadar(radarCreative),
    };
  }

  if (idFormat === ADVERT_FORMAT_TYPES.UNPRICED_BRAND_INLINE) {
    return {
      ...advertProps,
      creative: getFlightsUnpricedBrandInlineCreativeFromRadar(radarCreative),
    };
  }

  if (idFormat === ADVERT_FORMAT_TYPES.ITINERARY_INLINE_PLUS) {
    return {
      ...advertProps,
      creative: getItineraryInlinePlusCreativeFromRadar(radarCreative),
    };
  }

  if (idFormat === ADVERT_FORMAT_TYPES.FLEXIBLE_SEARCH) {
    return {
      ...advertProps,
      creative: getFlexibleSearchCreativeFromRadar(radarCreative),
    };
  }

  if (idFormat === ADVERT_FORMAT_TYPES.BANNER) {
    return {
      ...advertProps,
      creative: getBannerCreativeFromRadar(radarCreative),
    };
  }

  return null;
};
